import React, {CSSProperties, useContext, useState} from 'react';
import styles from './Login.module.scss'
import {Navigate} from 'react-router-dom';
import {Alert, Button, Col, Form, Input, Row, theme, Typography} from 'antd';
import {EyeInvisibleOutlined, EyeTwoTone, IconSpin, LockOutlined, UserOutlined} from "../../icons/icons";
import {doAuth, storeToLocalStorage} from "../../tools/Tools";
import MFACodeInput from "../../components/MFACodeInput";
import Redirector from "../../tools/Redirector";
import {GoogleLogin} from '@react-oauth/google';
import {AppConfig} from "../../Config";
import {useGlobalConfig} from "../../hooks/commonHooks";
import {ThemeContext, ThemeTypes} from "../../context/ThemeContext";
import {MoonOutlined, SunOutlined} from "@ant-design/icons";

const { useToken } = theme;

const {Title} = Typography;

const token = localStorage.getItem('token');
const redirectUrl = Redirector.getUri();

const Login = () => {

  const {debug} = useGlobalConfig();
  const {theme, themeType, setThemeType} = useContext(ThemeContext);

  const [authenticated, setAuthenticated] = useState(false);
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const [showMfa, setShowMfa] = useState<boolean>(false);
  const [wait, setWait] = useState<boolean>(false);
  const [mfaError, setMfaError] = useState(false);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [collapsedDebug, setcollapsedDebug] = useState<boolean>(true);

  const themeToken = useToken()?.token;

  const onFormSubmit = (values: any): void => {
    setError(null);
    if (values === null) {
      values = form.getFieldsValue();
    }
    if (idToken !== null) {
      values['id_token'] = idToken;
      delete values['email'];
      delete values['password'];
    }
    setWait(true);
    doAuth(values)
      .then(data => {
        if (data.token) {
          storeToLocalStorage('token', data.token);
          setAuthenticated(true);
        }
      })
      .catch(err => {
        setWait(false);
        switch (err.code) {
          case "A403":
            setError(err.message);
            setShowMfa(false);
            break;
          case "M403":
            setError(null);
            !showMfa && setShowMfa(true);
            break;
          case "M404":
          case "M405":
            setError(err.message);
            setShowMfa(true);
            setMfaError(true);
            break;
          case "R404":
            setError("Account not found");
            setIdToken(null);
            setShowMfa(false);
            setMfaError(false);
            break;
          case "GL403":
            setError(err.message);
            setIdToken(null);
            break;
          default:
            setError(err.message);
            break;
        }
        localStorage.clear();
      });
  };

  const style: CSSProperties = {
    display: showMfa ? "none" : ""
  };

  if (token || authenticated) return <Navigate to={redirectUrl}/>;

  return (
    <>
      {
          <Row className={styles.container}>
            <div className={styles.formContainer}>
              <div className={styles.logo}>
                <IconSpin style={{fontSize: 140}}/>
              </div>
              <Title className={styles.title} level={3}>
                Sign in as personal Google account
              </Title>
              {
                error !== null && !showMfa &&
                  <Alert message={error} type={"error"} style={{marginBottom: '24px'}}/>
              }
              <Form
                form={form}
                className={styles.form}
                layout="vertical"
                onFinish={onFormSubmit}
              >
                {
                  (!showMfa || !idToken) &&
                    <>
                        <Form.Item name="email" style={style} rules={[{required: true}]}>
                            <Input
                                size="large"
                                placeholder="Enter your ElBackup ID"
                                prefix={<UserOutlined/>}
                                spellcheck={false}
                                disabled={wait}
                            />
                        </Form.Item>
                        <Form.Item name="password" style={style} rules={[{required: true}]}>
                            <Input.Password
                                size="large"
                                placeholder="Enter your password"
                                iconRender={visible => (visible ? <EyeTwoTone/> : <EyeInvisibleOutlined/>)}
                                prefix={<LockOutlined/>}
                                disabled={wait}
                            />
                        </Form.Item>
                    </>
                }
                {
                  showMfa &&
                    <Form.Item name={"mfa_code"}>
                        <MFACodeInput
                            title={<h3 style={{textAlign: "center"}}>MFA Required</h3>}
                            numInputs={6}
                            description={"Enter 6 digit code from your app"}
                            hasError={mfaError}
                            errorMessage={error}
                            onChange={(v) => {
                              setMfaError(false);
                              setError(null);
                            }}
                        />
                    </Form.Item>
                }
                <Form.Item
                  style={{textAlign: showMfa ? "center" : "left"}}
                  className={styles.buttons}
                >
                  <Button
                    type="primary"
                    htmlType={'submit'}
                    onClick={() => onFormSubmit(null)}
                    disabled={wait}
                    size={"large"}
                  >
                    {showMfa ? "Continue" : "Login"}
                  </Button>
                  {
                    !showMfa &&
                      <div
                          style={{
                            float: "right",
                            marginLeft: "15px",
                            opacity: wait ? 0.5 : 1,
                            pointerEvents: wait ? 'none' : 'auto'
                          }}
                      >
                          <GoogleLogin
                              useOneTap={true}
                              theme="filled_blue"
                              onSuccess={creeds => {
                                if (creeds.credential !== undefined) {
                                  setIdToken(creeds.credential);
                                  onFormSubmit({
                                    "id_token": creeds.credential
                                  })
                                }
                              }}
                              onError={() => {
                                setIdToken(null);
                                setWait(false);
                              }}
                          />
                      </div>
                  }
                </Form.Item>
              </Form>
            </div>
          </Row>
      }
      <div className={styles.theme}>
        <Button
          className={`${styles.theme__button}${themeType === ThemeTypes.LIGHT ? ' ' + styles.theme__buttonSelected : ''}`}
          type='text'
          onClick={() => setThemeType(themeType === ThemeTypes.LIGHT ? ThemeTypes.SYSTEM : ThemeTypes.LIGHT)}
        >
          <SunOutlined className={styles.theme__button__icon}/>
        </Button>
        <Button
          className={`${styles.theme__button}${themeType === ThemeTypes.DARK ? ' ' + styles.theme__buttonSelected : ''}`}
          type='text'
          onClick={() => setThemeType(themeType === ThemeTypes.DARK ? ThemeTypes.SYSTEM : ThemeTypes.DARK)}
        >
          <MoonOutlined className={styles.theme__button__icon}/>
        </Button>
      </div>
    </>
  );
};

export default Login;